<template>
  <div>
    <slot :open-modal="openModal" />

    <PortalModal :open="modalOpen" ref="modal" @cancel="onCancel">
      <OverlayCard class="confirm-modal" @cancel="onCancel" data-test-id="confirm-modal">
        <template #title>{{ title }}</template>

        <template #content>
          <p v-if="message" class="overlay-card__name-grid">{{ message }}</p>

          <SelectionList
            v-if="options.length"
            data-test-id="confirm-modal-option"
            :options="selectionOptions"
            v-model="selectedOptionId"
            :multi="false"
            allow-empty-single-select
            :clickable-option="options.length === 1 ? 'custom-control' : true"
            density="dense"
            v-slot="{ label, isSelected, toggleSelection, index }"
          >
            <SelectableCard
              v-if="options.length === 1"
              :selected="isSelected"
              @click.stop="toggleSelection($event, label.value, index)"
              @keyup.enter="toggleSelection($event, label.value, index)"
              tabindex="0"
              boxed
            >
              <template #title>
                {{ label.title }}
              </template>

              <template #text>
                {{ label.description }}
              </template>

              <template #input>
                <CheckBox :model="isSelected" :modelValue="true" :checked="isSelected" />
              </template>
            </SelectableCard>
            <div v-else v-html="label.title" />
          </SelectionList>
        </template>

        <template #footer>
          <Btn
            type="submit"
            :color="destructive ? 'destructive' : 'blue'"
            size="medium"
            @click="invertButtonActions ? onCancel() : onConfirm()"
            :disabled="options.length > 1 && selectedOptionId === ''"
            data-test-id="confirm-submit"
          >
            {{ buttonText }}
          </Btn>
          <Btn
            color="grey"
            data-test-id="confirm-secondary-button"
            size="medium"
            @click="invertButtonActions ? onConfirm() : onCancel()"
          >
            {{ computedSecondaryButtonText }}
          </Btn>
        </template>
      </OverlayCard>
    </PortalModal>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import { ConfirmOption } from '@/types';
import PortalModal from '@components/PortalModal.vue';
import type { ListOptions } from './common/SelectionList.vue';

export default defineComponent({
  name: 'ConfirmModal',
  emits: { 'update:modalOpen': null },

  components: {
    PortalModal,
  },

  props: {
    title: {
      type: String,
      required: true,
    },

    message: {
      type: String,
      default: '',
    },

    buttonText: {
      type: String,
      required: true,
    },

    secondaryButtonText: {
      type: String,
      default: '',
    },

    options: {
      type: Array as PropType<Array<ConfirmOption>>,
      default: (): Array<ConfirmOption> => [],
    },

    /**
     * Inverts the button actions. i.e. primary button cancels, secondary button confirms
     */
    invertButtonActions: {
      type: Boolean,
      default: false,
    },

    /**
     * Gives the confirm button the destructive color (e.g. for deletions)
     */
    destructive: {
      type: Boolean,
      default: false,
    },

    /**
     * Use this prop to force the closing of the modal.
     * This is, if you want to programatically close it before
     * the user clicks on any button.
     */
    forceClose: {
      type: Boolean,
      default: false,
    },
  },

  data(): {
    modalOpen: boolean;
    callback: (selectedOptionId: string | Symbol) => void;
    selectedOptionId: string | Symbol;
  } {
    return {
      modalOpen: false,
      callback: null,
      selectedOptionId: '',
    };
  },

  computed: {
    /**
     * We need to define this computed property because the default value
     * for the secondary button is accessed via "this", and it cannot be set
     * on props.
     */
    computedSecondaryButtonText(): string {
      return this.secondaryButtonText || this.$t('action.cancel');
    },

    selectionOptions(): ListOptions {
      return this.options.map((option) => ({
        label: {
          ...option,
        },
        value: option.id,
      }));
    },
  },

  methods: {
    openModal(callback: () => void) {
      this.updateModalOpen(true);

      this.callback = callback;
    },

    closeModal() {
      this.updateModalOpen(false);

      this.callback = null;
    },

    onConfirm() {
      this.callback(this.selectedOptionId);
      this.selectedOptionId = '';
      this.closeModal();
    },

    onCancel() {
      this.selectedOptionId = '';
      this.closeModal();
    },

    updateModalOpen(value: boolean) {
      this.modalOpen = value;
      this.$emit('update:modalOpen', value);
    },
  },

  watch: {
    forceClose(value) {
      if (value === true) {
        this.closeModal();
      }
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'mixins/typography';
.confirm-modal {
  min-width: 320px;
  max-width: 424px;
}

.confirm-modal__options {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.confirm-modal__option {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  gap: 16px;
  border: 1px solid colors.$color-grey-200;
  border-radius: 8px;
}

.confirm-modal__option-text {
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 4px;
}

.confirm-modal__option-text-title {
  @include typography.typography-14-medium(160%);
  padding: 1px 0px 0px;
}

.confirm-modal__option-text-desc {
  @include typography.typography-14-normal(160%);
  color: colors.$color-grey-500;
}
</style>
